import { clsx } from 'clsx';
import { forwardRef, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { spacing } from '../../styles/spacing.css';
import { Flex } from '../Flex/Flex';
import { Text } from '../Text/Text';
import type { UsePhoneProps } from './hook/usePhone';
import * as styles from './Phone.css';
import type { SupportedCountryType } from './utils';
import { getMaskPhoneNum } from './utils';

export type PhoneInputProps = Omit<React.ComponentPropsWithRef<'input'>, 'onBlur'> &
  Pick<UsePhoneProps, 'hasError' | 'isBlurred' | 'onBlur' | 'onFocus' | 'placeholder'> & {
    readonly country: SupportedCountryType;
  };

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ country, value, placeholder, disabled, hasError, onBlur, onFocus, isBlurred, ...props }, ref) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const parsedPhoneNum = getMaskPhoneNum(value as string, country.countryCallingCode);

    const hasFloatingLabel = (value != null && value.toString().length > 0) || !isBlurred;

    return (
      <Flex height={spacing.s56} overflow="hidden" paddingInlineStart={spacing.s16} width="100%">
        <Text
          className={clsx(styles.phoneInputLabel, { floating: hasFloatingLabel })}
          color={hasError ? '$textNegative' : disabled ? '$textDisabled' : '$textTertiary'}
          onClick={() => internalRef.current?.focus()}
          type={hasError ? 'label' : hasFloatingLabel ? 'bodyTiny' : 'bodyMedium'}
        >
          {placeholder}
        </Text>
        <Flex alignItems="center" className={clsx(styles.phoneTextContainer, { floating: hasFloatingLabel })}>
          <Text
            className={clsx(styles.phonePrefix, { floating: hasFloatingLabel })}
            color={disabled ? '$textDisabled' : '$textTertiary'}
            onClick={() => internalRef.current?.focus()}
            type="bodyMedium"
          >
            (+{country.countryCallingCode})
          </Text>
          <input
            className={clsx(styles.phoneNumberInput({ hasError, disabled }), { floating: hasFloatingLabel })}
            disabled={disabled}
            onBlur={onBlur}
            onFocus={onFocus}
            placeholder={country.placeholder}
            ref={mergeRefs([internalRef, ref])}
            type="tel"
            width="100%"
            {...props}
            value={parsedPhoneNum}
          />
        </Flex>
      </Flex>
    );
  },
);
